import { postprocessClinicalHtml } from '../lib/clinicalText';
import { parseBlock, type NdjsonBlock } from '../lib/ndjsonBlockTypes';
import { ndjsonToHtml } from '../lib/ndjsonToHtml';

/**
 * Decide how to render a draft's raw `content` into the Tiptap editor.
 *
 * - **NDJSON** (the AI worker's freshly-saved shape) → parse to blocks
 *   and run `ndjsonToHtml`, which applies the full markdown-stage
 *   preprocessor (timestamp reformat + quote emphasis).
 * - **HTML** (already-edited drafts; first keystroke autosaves Tiptap
 *   output) → still pass through `postprocessClinicalHtml` so the
 *   timestamp display fix applies retroactively to content saved
 *   before the preprocessor existed.
 *
 * Detection: the first non-empty line must parse to a typed
 * `NdjsonBlock`. Idempotent: HTML output of either path passes through
 * the HTML branch unchanged on a second pass (the regex only catches
 * un-converted timestamps).
 */
export function aiDraftToEditorContent(rawContent: string | null | undefined): string | null {
  if (rawContent === null || rawContent === undefined) return null;
  if (rawContent.length === 0) return rawContent;

  const lines = rawContent.split('\n');
  const firstNonBlank = lines.find((l) => l.trim().length > 0);
  if (firstNonBlank === undefined) return rawContent;

  const firstBlock = parseBlock(firstNonBlank.trim());
  if (firstBlock === null) {
    // HTML path — still post-process so legacy saved drafts get the
    // readable timestamp treatment.
    return postprocessClinicalHtml(rawContent);
  }

  const blocks: NdjsonBlock[] = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (trimmed.length === 0) continue;
    const block = parseBlock(trimmed);
    if (block !== null) blocks.push(block);
  }

  return ndjsonToHtml(blocks);
}
